import React, { useContext, useState } from 'react';
import { UnitLinksService } from 'core/api/services/unitLinks';
import { toast } from 'react-toastify';
import { isValidPositiveInteger } from '@utriper/core';
import { useDebounce } from '@utriper/core-ui';

const UnitLinksContext = React.createContext(null);

export const UnitLinksProvider = ({ children }) => {
    const [loadingExpLink, setLoadingExpLink] = useState(false);
    const [expLinks, setExpLinks] = useState([]);
    const [totalExpLinks, setTotalExpLinks] = useState(0);
    const [totalExpLinksCount, setTotalExpLinksCount] = useState(0);
    const [submittingExpLink, setSubmittingExpLink] = useState(false);

    const initialExpFilters = {
        name: '',
        unitType: [],
        isActive: 'all',
    };
    const [expLinkFilters, setExpLinkFilters] = useState({ ...initialExpFilters });
    const [expLinkPage, setExpLinkPage] = useState(1);
    const expNameFilterDebounce = useDebounce(expLinkFilters?.name, 300);
    const MAX_PAGE = 12;

    const resetExpLinkFilters = () => {
        setExpLinkFilters({ ...initialExpFilters });
        setExpLinkPage(1);
    };

    const getExperienceLinks = async (experienceId) => {
        try {
            setLoadingExpLink(true);
            const options = { ...expLinkFilters, name: expNameFilterDebounce, pager: { page: expLinkPage, pageSize: MAX_PAGE } };

            const { units, total, totalCount } = await UnitLinksService.getExperienceLink(experienceId, options);

            if (!Array.isArray(units) || !Number.isInteger(total)) {
                throw new Error('Hubo un error obteniendo los tipos de pasajeros');
            }

            setExpLinks([...units]);
            setTotalExpLinks(total);
            setTotalExpLinksCount(totalCount);

            return true;
        } catch (error) {
            console.error(error?.message);

            return false;
        } finally {
            setLoadingExpLink(false);
        }
    };

    const parseLinkData = (linkData) => {
        if (linkData?.minPerBooking) {
            linkData.minPerBooking = parseInt(linkData.minPerBooking) || null;
        }

        if (linkData?.maxPerBooking) {
            linkData.maxPerBooking = parseInt(linkData.maxPerBooking) || null;
        }

        if (linkData?.capacity) {
            linkData.capacity = parseInt(linkData.capacity) || null;
        }

        return linkData;
    };

    const addExperienceLink = async (experienceId, linkData) => {
        try {
            setSubmittingExpLink(true);

            const experienceLinkData = parseLinkData(linkData);

            const result = await UnitLinksService.addExperienceLink(experienceId, experienceLinkData);

            if (!isValidPositiveInteger(result?.id)) {
                throw new Error('Hubo un error agregando el tipo de pasajero');
            }

            resetExpLinkFilters();
            setExpLinks((prev) => [...prev, result]);
            setTotalExpLinks(totalExpLinks + 1);

            toast.success('Tipo de Pasajero agregado con exito!', {
                position: 'top-right',
                theme: 'colored',
            });

            return true;
        } catch (error) {
            console.error(error?.message);

            return false;
        } finally {
            setSubmittingExpLink(false);
        }
    };

    const updateExperienceLink = async (experienceId, linkData) => {
        try {
            setSubmittingExpLink(true);

            const experienceLinkData = parseLinkData(linkData);

            const result = await UnitLinksService.updateExperienceLink(experienceId, experienceLinkData?.id, experienceLinkData);

            if (!isValidPositiveInteger(result?.id)) {
                throw new Error('Hubo un error actualizando el tipo de pasajero');
            }

            setExpLinks((prevExpLinks) => [
                ...prevExpLinks.map((expLink) => {
                    if (expLink?.id === result.id) {
                        return { ...result };
                    }

                    return expLink;
                }),
            ]);

            toast.success('Tipo de Pasajero actualizado con exito!', {
                position: 'top-right',
                theme: 'colored',
            });

            return true;
        } catch (error) {
            console.error(error?.message);

            return false;
        } finally {
            setSubmittingExpLink(false);
        }
    };

    return (
        <UnitLinksContext.Provider
            value={{
                getExperienceLinks,
                addExperienceLink,
                updateExperienceLink,
                submittingExpLink,
                loadingExpLink,
                expLinks,
                totalExpLinks,
                totalExpLinksCount,
                expLinkFilters,
                expNameFilterDebounce,
                setExpLinkFilters,
                expLinkPage,
                setExpLinkPage,
                MAX_PAGE,
                resetExpLinkFilters,
            }}
        >
            {children}
        </UnitLinksContext.Provider>
    );
};

UnitLinksProvider.displayName = 'UnitsLinkProvider';

export const useUnitLinks = () => useContext(UnitLinksContext);
